import React, { useState, useEffect } from 'react';
import { AlertTriangle, XCircle, Bell, X, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AlertBanner = ({ adminKey }) => {
  const [alerts, setAlerts] = useState([]);
  const [criticalCount, setCriticalCount] = useState(0);
  const [warningCount, setWarningCount] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAlerts();
    // Poll every 30 seconds
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, [adminKey]);

  const fetchAlerts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/alerts?status=active&limit=20`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
        setCriticalCount(data.critical_count || 0);
        setWarningCount(data.warning_count || 0);
      }
    } catch (err) {
      console.error('Error fetching alerts:', err);
    } finally {
      setLoading(false);
    }
  };

  const dismissAlert = async (alertId) => {
    try {
      const res = await fetch(`${API_URL}/api/alerts/${alertId}/dismiss`, {
        method: 'POST',
        headers: { 'X-Admin-Key': adminKey }
      });
      if (res.ok) {
        setAlerts(alerts.filter(a => a.id !== alertId));
        // Update counts
        const dismissed = alerts.find(a => a.id === alertId);
        if (dismissed?.severity === 'critical') {
          setCriticalCount(c => Math.max(0, c - 1));
        } else if (dismissed?.severity === 'warning') {
          setWarningCount(c => Math.max(0, c - 1));
        }
      }
    } catch (err) {
      console.error('Error dismissing alert:', err);
    }
  };

  if (loading || (criticalCount === 0 && warningCount === 0)) {
    return null;
  }

  const totalAlerts = criticalCount + warningCount;

  return (
    <div className="mb-6">
      {/* Collapsed Banner */}
      {!expanded && (
        <div 
          onClick={() => setExpanded(true)}
          className={`rounded-xl p-4 cursor-pointer transition-all ${
            criticalCount > 0 
              ? 'bg-red-50 border border-red-200 hover:bg-red-100' 
              : 'bg-amber-50 border border-amber-200 hover:bg-amber-100'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {criticalCount > 0 ? (
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
              ) : (
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
              )}
              <div>
                <p className={`font-medium ${criticalCount > 0 ? 'text-red-800' : 'text-amber-800'}`}>
                  {totalAlerts} Active Alert{totalAlerts !== 1 ? 's' : ''}
                </p>
                <p className={`text-sm ${criticalCount > 0 ? 'text-red-600' : 'text-amber-600'}`}>
                  {criticalCount > 0 && `${criticalCount} critical`}
                  {criticalCount > 0 && warningCount > 0 && ', '}
                  {warningCount > 0 && `${warningCount} warning`}
                </p>
              </div>
            </div>
            <ChevronRight className={`w-5 h-5 ${criticalCount > 0 ? 'text-red-400' : 'text-amber-400'}`} />
          </div>
        </div>
      )}

      {/* Expanded Alert List */}
      {expanded && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className={`px-4 py-3 flex items-center justify-between ${
            criticalCount > 0 ? 'bg-red-500' : 'bg-amber-500'
          }`}>
            <div className="flex items-center gap-2 text-white">
              <Bell className="w-5 h-5" />
              <span className="font-medium">Active Alerts ({totalAlerts})</span>
            </div>
            <button 
              onClick={() => setExpanded(false)}
              className="text-white/80 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Alert List */}
          <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
            {alerts.map(alert => (
              <div 
                key={alert.id} 
                className={`p-4 flex items-start justify-between ${
                  alert.severity === 'critical' ? 'bg-red-50' : 'bg-amber-50'
                }`}
              >
                <div className="flex items-start gap-3">
                  {alert.severity === 'critical' ? (
                    <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                  )}
                  <div>
                    <p className={`font-medium ${
                      alert.severity === 'critical' ? 'text-red-800' : 'text-amber-800'
                    }`}>
                      {alert.title}
                      {alert.count > 1 && (
                        <span className="ml-2 px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full">
                          ×{alert.count}
                        </span>
                      )}
                    </p>
                    <p className={`text-sm ${
                      alert.severity === 'critical' ? 'text-red-600' : 'text-amber-600'
                    }`}>
                      {alert.message}
                    </p>
                    {alert.target_name && (
                      <p className="text-xs text-gray-500 mt-1">
                        {alert.target_type}: {alert.target_name}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(alert.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => dismissAlert(alert.id)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  title="Dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
            <a 
              href="/admin/alerts"
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              View all alerts →
            </a>
          </div>
        </div>
      )}
    </div>
  );
};
